/**
 * Analytics — public API.
 *
 * One singleton `DualAnalytics` fanning out to GA4 + the Source Digital pixel.
 * Call sites use the semantic helpers below and never touch gtag / Pixel
 * directly. Every function is safe to call anywhere: when analytics is
 * disabled (no env config) or a sink is blocked, calls silently no-op.
 *
 * Usage:
 *   import analytics from '../services/analytics';
 *   analytics.trackLogin('password');
 *   const session = analytics.createVideoSession({ videoId, title, content_type });
 *   session.onTimeUpdate(currentTime);
 */

import DualAnalytics from './DualAnalytics';
import VideoSession from './videoSession';
import bindPlayer from './playerBinding';
import { EVENTS, contentContext, fighterContext } from './events';

const dual = new DualAnalytics();

const analytics = {
  init: () => dual.init(),
  setUserContext: ctx => dual.setContext(ctx),
  /** @param {Object} [sinks] - `{ ga, pixel }`; watch routes pass `{ pixel: false }`. */
  trackPageView: (url, title, sinks) => dual.pageView(url, title, sinks),
  track: (name, params, sinks) => dual.track(name, params, sinks),

  // --- Auth -----------------------------------------------------------------
  trackLogin: method => dual.track(EVENTS.LOGIN, { method }),
  trackLogout: () => dual.track(EVENTS.LOGOUT),
  trackSignUp: method => dual.track(EVENTS.SIGN_UP, { method }),

  // --- Subscription funnel --------------------------------------------------
  trackBeginCheckout: (params = {}) => dual.track(EVENTS.BEGIN_CHECKOUT, params),
  /** @param {Object} order - { productId, planName, value, currency } */
  trackPurchase: (order = {}) => {
    const params = {
      transaction_id: order.transactionId || order.orderId,
      value: order.value,
      currency: order.currency || 'USD',
      item_id: order.productId,
      item_name: order.planName,
    };
    dual.track(EVENTS.PURCHASE, params);
    // `subscribe` is a clearer key event for a subscription business.
    dual.track(EVENTS.SUBSCRIBE, params);
  },

  // --- Content engagement ---------------------------------------------------
  trackSelectContent: (item = {}, extra = {}) =>
    dual.track(EVENTS.SELECT_CONTENT, { ...contentContext(item), ...extra }),
  trackSearch: searchTerm => dual.track(EVENTS.SEARCH, { search_term: searchTerm }),
  trackAddToWatchlist: (item = {}) => dual.track(EVENTS.ADD_TO_WATCHLIST, contentContext(item)),
  trackRemoveFromWatchlist: (item = {}) =>
    dual.track(EVENTS.REMOVE_FROM_WATCHLIST, contentContext(item)),
  trackAddToFavorites: (item = {}) => dual.track(EVENTS.ADD_TO_FAVORITES, contentContext(item)),
  trackRemoveFromFavorites: (item = {}) =>
    dual.track(EVENTS.REMOVE_FROM_FAVORITES, contentContext(item)),

  // --- Fighters -------------------------------------------------------------
  trackFighterView: (fighter = {}) =>
    dual.track(EVENTS.FIGHTER_PROFILE_VIEW, fighterContext(fighter)),
  trackFollowFighter: (fighter = {}) => dual.track(EVENTS.FOLLOW_FIGHTER, fighterContext(fighter)),
  trackUnfollowFighter: (fighter = {}) =>
    dual.track(EVENTS.UNFOLLOW_FIGHTER, fighterContext(fighter)),

  // --- Video ----------------------------------------------------------------
  /**
   * Watch page opened — the video-journey entry event (video_view → video_start
   * → progress → complete). Pixel-only: GA4 keeps its page_view for these routes
   * since it can't treat a custom event as a page view.
   * @param {Object} item - content/channel record (normalized via contentContext)
   */
  trackVideoView: (item = {}) => dual.track(EVENTS.VIDEO_VIEW, contentContext(item), { ga: false }),

  /**
   * Create a per-playback session that derives start/progress/complete/seek/
   * rebuffer/heartbeat from the player's timeupdate/pause/ended signals.
   * @param {Object} meta
   * @returns {VideoSession}
   */
  createVideoSession: (meta = {}) =>
    new VideoSession(meta, { emit: (name, params) => dual.track(name, params) }),

  /**
   * Bind a mounted DotPlayer to a full engagement + real-QoE video session
   * (video_start/progress/complete/heartbeat plus real
   * startup/rebuffer/seek/quality_change/error from native media events).
   * Call once the player is loaded; invoke the returned cleanup on unmount.
   * @param {Object} player - object returned by `window.DotPlayer.mount(...)`
   * @param {Object} meta - content context (content_id/title/type/series_id/duration)
   * @param {Object} [opts] - `{ targetSelector }` — the mount container (e.g. '#pdp-player')
   * @returns {() => void} cleanup
   */
  bindPlayer: (player, meta = {}, opts = {}) =>
    bindPlayer(player, meta, (name, params) => dual.track(name, params), opts),

  EVENTS,
};

export default analytics;
export { EVENTS };
